<template>
  <div class="JoszakiAutocomplete">
    <slot name="label" :failed="failed">
      <p
        v-if="label"
        class="text-base font-bold"
        :class="{
          'text-error': failed,
        }"
      >
        {{ label }}
      </p>
    </slot>
    <div class="flex flex-wrap w-full gap-2">
      <div ref="dropdownToggle" class="w-full">
        <JoszakiInputWrapper
          :disabled="_disabled"
          class="hover:cursor-pointer"
          @click.native.stop="downClicked"
        >
          <IconComponent
            :pack="iconPack"
            :icon="icon"
            class="text-gray-300 placeholder-gray-300 w-6 dark:text-gray-500"
          />

          <div class="flex-1 flex flex-row flex-wrap gap-2">
            <template v-if="isMultiSelect">
              <slot name="tags">
                <JoszakiTooltip
                  v-for="(item, index) in internalValue"
                  :key="index"
                  :text="pillTitleFor(item)"
                >
                  <JoszakiPill
                    :type="pillType"
                    :inverted="isPillInverted(item)"
                    class="cursor-default text-xs"
                  >
                    {{ formattedItem(item) }}
                    <IconComponent
                      icon="times-circle"
                      class="inline cursor-pointer"
                      @click.native="() => removeItem(item)"
                    />
                  </JoszakiPill>
                </JoszakiTooltip>
              </slot>
            </template>

            <input
              ref="input"
              :value="
                showDropdown || isMultiSelect
                  ? searchQuery
                  : formattedItem(internalValue)
              "
              class="focus:outline-none flex-1 w-auto min-w-[100px] dark:bg-transparent dark:text-gray-200 dark:placeholder-gray-500"
              :class="{ '!bg-gray-100 dark:!bg-gray-600': _disabled }"
              :placeholder="actualPlaceholder"
              :disabled="_disabled"
              @input="onInput"
              @click.stop="inputClicked"
              @touchend="inputClicked"
              @keydown.stop="onKeyDown"
              @focus="inputFocused"
            />
          </div>
          <div
            v-if="!hideDownIcon"
            class="h-full flex items-center cursor-pointer text-gray-400 dark:text-gray-500"
          >
            <IconComponent icon="angle-down" @click.native.stop="downClicked" />
          </div>
          <div
            v-if="shouldShowClearIcon"
            class="h-full flex items-center cursor-pointer text-gray-400 dark:text-gray-500"
          >
            <IconComponent icon="times" @click.native.stop="clear" />
          </div>
        </JoszakiInputWrapper>
      </div>

      <div
        v-show="showDropdown && searchQuery.length >= minSearchLength"
        ref="dropdownMenu"
        class="bg-white rounded-md z-10 border border-gray-300 shadow-md dark:bg-gray-800 dark:border-gray-600"
      >
        <div
          ref="container"
          class="max-h-52 overflow-y-auto"
          :class="{
            'py-2': !groupIdField,
          }"
          @scroll="onScroll"
        >
          <JoszakiLoading :active="loading" />
          <slot name="topActions" :search-query="searchQuery" />
          <template v-if="groups.length">
            <div
              v-if="allowNew && searchQuery"
              class="max-md:py-2 md:py-1 pl-3 pr-2 cursor-pointer hover:bg-gray-100 text-sm text-black dark:hover:bg-gray-700 dark:text-gray-100"
              @click.stop.prevent="selectNewItem"
            >
              {{
                newItemLabelKey
                  ? $t(newItemLabelKey, { item: searchQuery })
                  : searchQuery
              }}
            </div>
            <div v-for="group of groups" :key="`group-${group.id}`">
              <div
                v-if="groupValueField && groupedSortedItems[group.id]?.length"
                class="font-bold p-2 text-black dark:text-gray-100"
              >
                {{ group.name }}
              </div>
              <div
                v-for="item of groupedSortedItems[group.id]"
                :key="`group-${group.id}-item-${get(item, idField)}`"
                :ref="get(item, idField)"
                class="max-md:py-2 md:py-1 pl-3 pr-2 cursor-pointer hover:bg-gray-100 text-sm text-black dark:hover:bg-gray-700 dark:text-gray-100"
                :class="{
                  'bg-gray-100 dark:bg-gray-700': indexId === get(item, idField),
                }"
                @click.stop.prevent="selectItem(item)"
              >
                <IconComponent
                  v-if="item.icon"
                  class="inline w-4 h-4 mr-1 text-primary"
                  :icon="item.icon"
                  :pack="item.iconPack"
                  :class="item.iconClass"
                />
                {{ formatter ? formatter(item) : get(item, valueField) }}
              </div>
            </div>
          </template>
          <template v-else>
            <div class="py-1 pl-3 pr-2 cursor-pointer text-sm">
              <slot name="empty" />
            </div>
          </template>
          <slot name="footer" :search-query="searchQuery" />
        </div>
      </div>
    </div>
    <p
      v-if="showErrorMsg"
      class="text-sm text-error"
      :class="{
        invisible: !failed,
      }"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { createPopper } from "@popperjs/core";
import { inject } from "@nuxtjs/composition-api";
import get from "lodash/get";
import flatten from "lodash/flatten";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import deburr from "lodash/deburr";
import isArray from "lodash/isArray";
import debounce from "lodash/debounce";
import JoszakiInputWrapper from "./input/JoszakiInputWrapper.vue";
import JoszakiTooltip from "./JoszakiTooltip.vue";
import { excludeSelectedItems } from "~/helpers/generic";

const SCROLL_END_THRESHOLD = 24;

const sameWidth = {
  name: "sameWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
  },
};

export default {
  components: { JoszakiInputWrapper, JoszakiTooltip },
  props: {
    value: {
      type: [Array, Object],
      default: () => null,
    },
    placeholder: {
      type: String,
      default: "",
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    icon: {
      type: String,
      default: "search",
    },
    iconPack: {
      type: String,
      default: "fas",
    },
    idField: {
      type: String,
      default: "id",
    },
    valueField: {
      type: String,
      default: "name",
    },
    formatter: {
      type: Function,
      default: null,
    },
    itemsComparator: {
      type: [Function, Array, String],
      default: null,
    },
    groupIdField: {
      type: String,
      default: null,
    },
    groupValueField: {
      type: String,
      default: null,
    },
    groupComparator: {
      type: [Function, Array, String],
      default: null,
    },
    keepFirst: {
      type: Boolean,
      default: true,
    },
    validationState: {
      type: Object,
      required: false,
      default: () => {},
    },
    label: {
      type: String,
      default: null,
    },
    showErrorMsg: {
      type: Boolean,
      default: true,
    },
    reserveErrorSpace: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxItems: {
      type: Number,
      default: 0,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    disableSort: {
      type: Boolean,
      default: false,
    },
    hideDownIcon: {
      type: Boolean,
      default: false,
    },
    localFilter: {
      type: Boolean,
      default: false,
    },
    pillType: {
      type: String,
      default: "primary",
    },
    // Boolean for all pills, or a function (item) => Boolean for per-item styling
    pillInverted: {
      type: [Boolean, Function],
      default: false,
    },
    // String for all pills, or a function (item) => String for per-item tooltips
    pillTitle: {
      type: [String, Function],
      default: "",
    },
    allowNew: {
      type: Boolean,
      default: false,
    },
    newItemLabelKey: {
      type: String,
      default: null,
    },
    minSearchLength: {
      type: Number,
      default: 0,
    },
    // in multi-select mode, hide items that are already selected from the
    // dropdown (matched by idField) instead of showing them as no-op options
    excludeSelected: {
      type: Boolean,
      default: false,
    },
  },
  setup(_props, { emit }) {
    const { disabled: formDisabled } = inject("form", { disabled: false });

    const debouncedEmitSearch = debounce((searchQuery) => {
      emit("search", searchQuery);
    }, 200);

    return {
      formDisabled,
      get,
      debouncedEmitSearch,
    };
  },
  data() {
    return {
      selectedIndex: 0,
      showDropdown: false,
      internalValue: this.value,
      searchQuery: "",
    };
  },
  computed: {
    actualPlaceholder() {
      if (this.isMultiSelect) {
        return this.placeholder;
      }
      const placeholder = this.internalValue
        ? this.formattedItem(this.internalValue)
        : this.placeholder;

      return this.internalValue && !this.showDropdown ? "" : placeholder;
    },
    isMultiSelect() {
      return Array.isArray(this.value);
    },
    _disabled() {
      return this.disabled || this.formDisabled;
    },
    indexItem() {
      return this.flattenedArray[this.selectedIndex];
    },
    indexId() {
      return get(this.indexItem, this.idField);
    },
    actualItemsComparator() {
      const indexComparator = (item) => {
        const deburredSearchValue = deburr(this.searchQuery).toLowerCase();
        const deburredItemValue = deburr(
          get(item, this.valueField)
        ).toLowerCase();

        const index = deburredItemValue.indexOf(deburredSearchValue);
        return index === -1 ? 1000 : index;
      };

      const alphabeticalComparator = (item) =>
        deburr(get(item, this.valueField)).toLowerCase();

      if (this.itemsComparator) {
        return [
          ...(isArray(this.itemsComparator)
            ? this.itemsComparator
            : [this.itemsComparator]),
          indexComparator,
          alphabeticalComparator,
        ];
      }

      return [indexComparator, alphabeticalComparator];
    },
    flattenedArray() {
      return flatten(Object.values(this.groupedSortedItems));
    },
    groupedSortedItems() {
      let sortedItems = this.items;

      if (!this.disableSort) {
        sortedItems = sortBy(this.items, this.actualItemsComparator);
      }

      if (this.localFilter) {
        sortedItems = sortedItems.filter((item) =>
          this.$helpers
            .normalizeInput(get(item, this.valueField))
            .includes(this.$helpers.normalizeInput(this.searchQuery))
        );
      }

      if (
        this.excludeSelected &&
        this.isMultiSelect &&
        Array.isArray(this.internalValue)
      ) {
        sortedItems = excludeSelectedItems(
          sortedItems,
          this.internalValue,
          this.idField
        );
      }

      if (!this.groupIdField) {
        return {
          null: sortedItems,
        };
      }

      return groupBy(sortedItems, this.groupIdField);
    },
    groups() {
      if (!this.groupIdField) {
        return [{ id: null, value: null }];
      }

      const itemsOrderedByGroup = sortBy(
        this.items,
        this.groupComparator ??
          ((item) => deburr(get(item, this.groupValueField)))
      );
      const mappedGroups = itemsOrderedByGroup.map((item) => {
        return {
          id: get(item, this.groupIdField),
          name: get(item, this.groupValueField),
        };
      });

      return uniqBy(mappedGroups, "id");
    },
    errorMessage() {
      return (
        this.validationState?.$errors?.[0]?.$message ??
        (this.reserveErrorSpace
          ? "placeholder so height is correctly computed"
          : "")
      );
    },
    failed() {
      return this.validationState?.$error;
    },
    shouldShowClearIcon() {
      return (
        this.clearable &&
        this.internalValue &&
        !this.isMultiSelect &&
        !this._disabled
      );
    },
  },
  watch: {
    internalValue(newValue) {
      this.$emit("input", newValue);
    },
    value(newValue) {
      this.internalValue = newValue;
    },
    searchQuery(newValue) {
      this.debouncedEmitSearch(newValue);
    },
  },
  mounted() {
    document.addEventListener("mouseup", this.outsideClickHandler);
    if (this.autofocus) {
      this.focusInput();
    }
  },
  destroyed() {
    document.removeEventListener("mouseup", this.outsideClickHandler);
    if (this.popper) {
      this.popper.destroy();
    }
  },
  methods: {
    lazyInitPopper() {
      if (!this.popper) {
        this.popper = createPopper(
          this.$refs.dropdownToggle,
          this.$refs.dropdownMenu,
          {
            placement: "bottom-start",
            modifiers: [sameWidth],
          }
        );
      }
    },
    openDropdown() {
      this.showDropdown = true;
      this.setPopperEventListeners(true);
    },
    hideDropdown() {
      this.showDropdown = false;
      this.setPopperEventListeners(false);
    },
    setPopperEventListeners(enabled) {
      this.lazyInitPopper();
      this.popper.setOptions((options) => ({
        ...options,
        modifiers: [...options.modifiers, { name: "eventListeners", enabled }],
      }));
    },
    onInput(e) {
      this.searchQuery = e.target.value;
      this.selectedIndex = 0;
      this.openDropdown();
    },
    clear() {
      this.internalValue = this.isMultiSelect ? [] : null;
      this.searchQuery = "";
    },
    inputFocused() {
      this.openDropdown();
      this.$emit("focus", this.searchQuery);
    },
    focusInput() {
      this.$refs.input.focus();
      this.$nextTick(() => {
        this.openDropdown();
      });
    },
    isContainer(el) {
      return el === this.$refs.container;
    },
    onScroll(e) {
      const el = e.target;
      const distanceToBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceToBottom <= SCROLL_END_THRESHOLD) {
        this.$emit("scroll-end");
      }
    },
    outsideClickHandler(e) {
      if (!this.$refs.dropdownToggle.contains(e.target) && this.showDropdown) {
        this.hideDropdown();
        this.$emit("blur");

        if (this.allowNew && this.searchQuery?.length) {
          this.selectNewItem();
        }
      }
    },
    inputClicked() {
      this.showDropdown = true;
    },
    onKeyDown(e) {
      if (e.key === "ArrowDown") {
        this.selectedIndex = Math.min(
          this.flattenedArray.length - 1,
          this.selectedIndex + 1
        );
        const el = this.$refs[this.indexId]?.[0];
        this.checkAndScroll(el);
      } else if (e.key === "ArrowUp") {
        this.selectedIndex = Math.max(0, this.selectedIndex - 1);
        const el = this.$refs[this.indexId]?.[0];
        this.checkAndScroll(el);
      } else if (e.key === "Enter") {
        if (this.allowNew && this.searchQuery && !this.flattenedArray.length) {
          this.selectNewItem();
        } else {
          this.selectItem(this.indexItem);
        }
      } else if (e.key === "Escape") {
        if (this.keepFirst) {
          this.selectedIndex = 0;
          this.$nextTick(() => this.selectItem(this.indexItem));
        }
        this.$refs.input.blur();
      }
    },
    downClicked() {
      if (this.showDropdown) {
        this.hideDropdown();
      } else {
        this.$refs.input.focus();
      }
    },
    selectItem(item) {
      if (!this.isMultiSelect) {
        this.internalValue = item;
        this.hideDropdown();
        this.searchQuery = "";
        this.$emit("select", item);
        return;
      }

      if (this.maxItems && this.internalValue.length >= this.maxItems) {
        return;
      }

      const isItemAlreadySelected = this.internalValue.find(
        (selected) => get(selected, this.idField) === get(item, this.idField)
      );

      if (isItemAlreadySelected) {
        return;
      }

      this.internalValue = [...this.internalValue, item];
      this.searchQuery = "";
      this.$emit("select", item);
    },
    selectNewItem() {
      const newItem = {
        [this.valueField]: this.searchQuery,
        [this.idField]: null,
      };
      this.selectItem(newItem);
    },
    isPillInverted(item) {
      return typeof this.pillInverted === "function"
        ? this.pillInverted(item)
        : this.pillInverted;
    },
    pillTitleFor(item) {
      return typeof this.pillTitle === "function"
        ? this.pillTitle(item)
        : this.pillTitle;
    },
    removeItem(item) {
      if (!this.isMultiSelect) {
        return;
      }
      this.internalValue = this.internalValue.filter(
        (i) => get(i, this.idField) !== get(item, this.idField)
      );
      this.$emit("remove", item);
    },
    checkAndScroll(el) {
      const rect = el.getBoundingClientRect();
      const top = rect.top;
      const bottom = rect.bottom;
      const container = this.$refs.container;
      const containerRect = container.getBoundingClientRect();
      const topWithinContainer = top >= containerRect.top;
      const bottomWithinContainer = bottom <= containerRect.bottom;
      if (!bottomWithinContainer) {
        container.scrollBy({
          top: rect.bottom - containerRect.bottom,
        });
      } else if (!topWithinContainer) {
        container.scrollBy({
          top: rect.top - containerRect.top,
        });
      }
    },
    formattedItem(item) {
      if (!item) {
        return "";
      }
      return this.formatter ? this.formatter(item) : get(item, this.valueField);
    },
  },
};
</script>
